import { ApolloClient, InMemoryCache } from "@apollo/client";
import { ApolloLink } from "@apollo/client/link";
import { SetContextLink } from "@apollo/client/link/context";
import { HttpLink } from "@apollo/client/link/http";
import { config } from "../config/env";
import { forceRefreshIdTokenForRecovery, getAppCheckHeaderToken, getRequestIdentity } from "./firebase";
import {
  NO_CREDENTIAL,
  SESSION_CREDENTIAL_CONTEXT_KEY,
  type SessionCredential,
  type SessionEndSignal,
} from "../../../../shared/graphql/sessionEnd";
import { FORCED_ID_TOKEN_CONTEXT_KEY, createSessionLink } from "../../../../shared/graphql/sessionLink";
import type { ForcedIdTokenRecovery } from "../../../../shared/graphql/tokenRecovery";
import { APP_CHECK_ONLY_OPERATIONS } from "./appOperations";

/**
 * Injected so the header and session behaviour can be unit-tested without
 * Firebase.
 */
export interface TokenProviders {
  /** The token for this request and whether it is a guest's (lib/firebase). */
  getRequestIdentity(): Promise<{ token: string | null; credential: SessionCredential }>;
  getAppCheckToken(): Promise<string | null>;
  /** Only ever called for a credentialed request, with its UID; refreshes that user only. */
  forceRefreshIdToken(uid: string): Promise<ForcedIdTokenRecovery>;
  /** Defaults to the shared session-end signal; IdentityProvider owns it. */
  onSessionEnd?(signal: SessionEndSignal): void;
}

/**
 * The client for the PUBLIC /app API.
 *
 * Deliberately simpler than apps/app's, and the differences are the point:
 *
 * - **A guest is never refreshed or signed out from here.** Signing out an
 *   anonymous guest destroys the UID that *is* their booking's `clientId` and
 *   orphans any hold they own; a guest has no password to sign back in with,
 *   so that is unrecoverable. A guest's 401 surfaces to the caller, as before.
 *
 * - **A credentialed client gets the shared session rules** (shared/graphql/
 *   sessionLink.ts): SESSION_REVOKED raises the session-end signal, which
 *   IdentityProvider acts on (deferred while a booking is sending or may
 *   exist); a 401 without a reason refreshes once and ends the session only
 *   when the Firebase SDK says the identity is invalid. A write is NEVER
 *   replayed from here: only a person pressing Check resends a booking,
 *   under the same key.
 *
 * - **A shape refusal** (400, GRAPHQL_VALIDATION_FAILED / _PARSE_FAILED) is a
 *   client bug: never retried, reported once as a fixed message.
 *
 * - **The ID token is optional, and never sent on a public read.** The
 *   operations in lib/appOperations.ts's `app-check-only` list (the slug, the
 *   profile, the slots, the Terms) carry App Check only, whoever is signed in.
 *   Holds and booking writes carry the token once a guest or account
 *   identity exists.
 *
 * Header contract (docs/integrations/backend-api.md):
 *   X-Firebase-AppCheck: <token>     hard-required, no grace period
 *   Authorization: Bearer <token>    only once an identity exists, never on an app-check-only read
 *   Apollo-Require-Preflight: true   CSRF preflight
 */
export function createApolloClient(
  providers: TokenProviders,
  uri: string = config.appUrl,
  /**
   * Operations sent with App Check only, never the caller's ID token
   * (lib/appOperations.ts). The /app client passes Book's list; the /core
   * client passes none.
   */
  appCheckOnly: ReadonlySet<string> = new Set()
): ApolloClient {
  const authLink = new SetContextLink(async (prevContext, operation) => {
    // The one replay after a refresh carries the refreshed token and keeps
    // the credential its first send recorded.
    const forced = prevContext[FORCED_ID_TOKEN_CONTEXT_KEY] as string | undefined;
    const anonymousRead = appCheckOnly.has(operation.operationName ?? "");
    const [identity, appCheckToken] = await Promise.all([
      anonymousRead
        ? // No token and no credential: the session rules never see this
          // request, and a revoked session cannot fail a public read.
          Promise.resolve({ token: null, credential: NO_CREDENTIAL })
        : forced
          ? Promise.resolve({
              token: forced,
              credential: (prevContext[SESSION_CREDENTIAL_CONTEXT_KEY] as SessionCredential | undefined) ?? NO_CREDENTIAL,
            })
          : providers.getRequestIdentity(),
      providers.getAppCheckToken(),
    ]);
    const idToken = identity.token;

    return {
      ...prevContext,
      [SESSION_CREDENTIAL_CONTEXT_KEY]: identity.credential,
      headers: {
        ...(prevContext.headers ?? {}),
        "Apollo-Require-Preflight": "true",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...(appCheckToken ? { "X-Firebase-AppCheck": appCheckToken } : {}),
      },
    };
  });

  const sessionLink = createSessionLink({
    forceRefreshIdToken: (uid) => providers.forceRefreshIdToken(uid),
    ...(providers.onSessionEnd ? { endSession: providers.onSessionEnd } : {}),
    // Only /api/v1/core sits behind the token-checking gateway (Backend, rc.3).
    gateway: new URL(uri).pathname.replace(/\/+$/, "").endsWith("/api/v1/core"),
  });

  return new ApolloClient({
    link: ApolloLink.from([sessionLink, authLink, new HttpLink({ uri })]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            // Keyed by slug so two professionals cannot share a cache entry,
            // and a MOVED redirect does not serve the old name's result to the
            // canonical one.
            resolveBookingSlug: { keyArgs: ["slug"] },
            getProfessionalProfile: { keyArgs: ["professionalId"] },
          },
        },
      },
    }),
  });
}

// Read at call time, not module load, so a test mocking lib/firebase with
// only the functions it uses can still import this module.
const firebaseProviders: TokenProviders = {
  getRequestIdentity: () => getRequestIdentity(),
  getAppCheckToken: () => getAppCheckHeaderToken(),
  forceRefreshIdToken: (uid) => forceRefreshIdTokenForRecovery(uid),
};

let clientInstance: ApolloClient | null = null;
let coreClientInstance: ApolloClient | null = null;

/** The shared client, wired to the real Firebase token providers. */
export function getApolloClient(): ApolloClient {
  if (clientInstance) return clientInstance;
  clientInstance = createApolloClient(firebaseProviders, config.appUrl, APP_CHECK_ONLY_OPERATIONS);
  return clientInstance;
}

/** Separate cache and endpoint for credentialed account.self reads. */
export function getCoreApolloClient(): ApolloClient {
  if (coreClientInstance) return coreClientInstance;
  coreClientInstance = createApolloClient(firebaseProviders, config.coreUrl);
  return coreClientInstance;
}
